import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { useTranslation } from 'react-i18next';
import { isString } from 'lodash';
import ReactQuill, { Quill } from 'react-quill';
import BlotFormatter from 'quill-blot-formatter';
import { addErrorNotification } from 'shared/lib/notifications';
import ApiClient from 'shared/lib/ApiClient';
import ErrorMessage from 'shared/components/ErrorMessage';
import Label from 'shared/components/Form/Label';
import ImageFormatter from './ImageFormatter';
import VideoFormatter from './VideoFormatter';
import Clearer from './Clearer';
import './quill.snow.css';
import styles from './Wysiwyg.module.scss';

Quill.debug(false); // set true or 'log' to debug

Quill.register({
  'modules/blotFormatter': BlotFormatter,
  'formats/image': ImageFormatter,
  'formats/video': VideoFormatter
});

Quill.register(Clearer, true);
const Delta = Quill.import('delta');

const Wysiwyg = ({
  apiClient,
  id,
  label,
  placeholder,
  value,
  onChange,
  error,
  labelSuffix
}) => {
  const ref = useRef();
  const { t, i18n } = useTranslation('common');

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];

      try {
        const { url } = await apiClient.post('/upload/image', {}, {}, [{ name: file.name, file }]);

        const range = ref.current.getEditorSelection();
        ref.current.getEditor().insertEmbed(range.index, 'image', url, 'user');
      } catch {
        addErrorNotification(t('wysiwyg.insertImageFailed'));
      }
    };
  };

  useEffect(() => {
    tippy('.ql-header.ql-picker', { content: t('wysiwyg.format'), arrow: true });
    tippy('.ql-align.ql-picker.ql-icon-picker', { content: t('wysiwyg.align'), arrow: true });
    tippy('.ql-bold', { content: t('wysiwyg.b'), arrow: true });
    tippy('.ql-italic', { content: t('wysiwyg.i'), arrow: true });
    tippy('.ql-underline', { content: t('wysiwyg.u'), arrow: true });
    tippy('.ql-strike', { content: t('wysiwyg.s'), arrow: true });
    tippy('.ql-blockquote', { content: t('wysiwyg.cite'), arrow: true });
    tippy('.ql-code-block', { content: t('wysiwyg.code'), arrow: true });
    tippy('.ql-script[value="sub"]', { content: t('wysiwyg.sub'), arrow: true });
    tippy('.ql-script[value="super"]', { content: t('wysiwyg.sup'), arrow: true });
    tippy('.ql-list[value="ordered"]', { content: t('wysiwyg.ol'), arrow: true });
    tippy('.ql-list[value="bullet"]', { content: t('wysiwyg.ul'), arrow: true });
    tippy('.ql-indent[value="-1"]', { content: t('wysiwyg.indentRight'), arrow: true });
    tippy('.ql-indent[value="+1"]', { content: t('wysiwyg.indentLeft'), arrow: true });
    tippy('.ql-link', { content: t('wysiwyg.link'), arrow: true });
    tippy('.ql-link', { content: t('wysiwyg.link'), arrow: true });
    tippy('.ql-image', { content: t('wysiwyg.img'), arrow: true });
    tippy('.ql-video', { content: t('wysiwyg.video'), arrow: true });
    tippy('.ql-clear-float', { content: t('wysiwyg.break'), arrow: true });
    tippy('.ql-clean', { content: t('wysiwyg.clean'), arrow: true });
  }, [i18n.language]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, 4, false] }],
        [{ align: [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
        [{ script: 'sub' }, { script: 'super' }],
        [
          { list: 'ordered' },
          { list: 'bullet' },
          { indent: '-1' },
          { indent: '+1' }
        ],
        ['link', 'image', 'video', 'clear-float', 'clean']
      ],
      handlers: {
        image: imageHandler,
        'clear-float': () => {
          const range = ref.current.getEditorSelection(true);
          ref.current.getEditor().insertEmbed(range.index, 'clearer', '.', 'user');
          ref.current.getEditor().setSelection(range.index + 2, 'user');
        }
      },
    },
    clipboard: {
      // toggle to add extra line breaks when pasting HTML:
      matchVisual: false,
      matchers: [
        ['H1', (node, delta) => delta.compose(new Delta().retain(delta.length(), { header: 2 }))],
        ['H5', (node, delta) => delta.compose(new Delta().retain(delta.length(), { header: 4 }))],
        ['H6', (node, delta) => delta.compose(new Delta().retain(delta.length(), { header: 4 }))],
        ['H7', (node, delta) => delta.compose(new Delta().retain(delta.length(), { header: 4 }))],
      ]
    },
    blotFormatter: {},
    history: {} // TODO: https://skilleto.atlassian.net/browse/SKL-749
  }), []);

  const formats = useMemo(() => [
    'header',
    'align',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'code-block',
    'script',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
    'video',
    'clearer'
    // 'height', 'width', 'style', 'data-align'
  ], []);

  const handleChange = (val, delta, author) => {
    if (author === 'user') {
      onChange(val);
    }
  };

  const handleChangeSelection = () => {
    setTimeout(() => {
      tippy('.blot-formatter__toolbar-button:first-child', { content: t('wysiwyg.imgLeft'), arrow: true });
      tippy('.blot-formatter__toolbar-button:nth-child(2)', { content: t('wysiwyg.imgCenter'), arrow: true });
      tippy('.blot-formatter__toolbar-button:nth-child(3', { content: t('wysiwyg.imgRight'), arrow: true });
    }, 300);
  };

  return (
    <div className={cn(styles.wysiwyg, { [styles.error]: !!error })}>
      {label !== null && <Label htmlFor={id} hasError={!!error} suffix={labelSuffix}>{label}</Label>}
      <ReactQuill
        ref={ref}
        id={id}
        className={cn(styles.editor, `ql-container--${i18n.language}`)}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        value={value}
        onChange={handleChange}
        onChangeSelection={handleChangeSelection}
      />
      {isString(error) && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

Wysiwyg.propTypes = {
  apiClient: PropTypes.instanceOf(ApiClient).isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  labelSuffix: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  // height: PropTypes.number
};

Wysiwyg.defaultProps = {
  id: 'wysiwyg',
  label: null,
  labelSuffix: null,
  placeholder: null,
  value: '',
  onChange() {},
  error: null,
  // height: 400
};

export default Wysiwyg;
